
import { useNews } from "@/hooks/useNews";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, Calendar, Megaphone, Newspaper, Eye } from "lucide-react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { isYouTubeURL } from "@/utils/videoUtils";
import { LazyYouTube } from "./LazyYouTube";
import { useI18n } from "@/i18n/LanguageContext";
import { pickLocalized } from "@/i18n/dbNames";
import { Reveal, Stagger, StaggerItem } from "@/components/motion";

const getNewsTypeIcon = (type: string) => {
  switch (type) {
    case 'event':
      return <Calendar className="h-4 w-4" aria-hidden="true" />;
    case 'announcement':
      return <Megaphone className="h-4 w-4" aria-hidden="true" />;
    default:
      return <Newspaper className="h-4 w-4" aria-hidden="true" />;
  }
};

export const News = () => {
  const { news, loading } = useNews();
  const { t, locale } = useI18n();

  const getNewsTypeLabel = (type: string) => {
    switch (type) {
      case 'event':
        return t.news.type.event;
      case 'announcement':
        return t.news.type.announcement;
      default:
        return t.news.type.news;
    }
  };

  // Show only 6 news items on home page
  const displayNews = news?.slice(0, 6) || [];

  if (loading) {
    return (
      <section className="app-section border-y border-border/40 bg-card/30" aria-busy="true">
        <div className="app-container">
          <div className="mb-12 text-center">
            <span className="section-kicker justify-center">{t.news.kicker}</span>
            <h2 className="section-title">{t.news.title}</h2>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[...Array(3)].map((_, i) => (
              <Card key={i} className="app-card animate-pulse overflow-hidden hover:translate-y-0">
                <div className="h-48 bg-muted"></div>
                <CardContent className="p-6">
                  <div className="mb-2 h-4 rounded bg-muted"></div>
                  <div className="mb-4 h-6 rounded bg-muted"></div>
                  <div className="h-20 rounded bg-muted"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!news || news.length === 0) {
    return null;
  }

  return (
    <section id="news" className="app-section scroll-mt-20 border-y border-border/40 bg-card/30">
      <div className="app-container">
        <Reveal className="mb-12 text-center">
          <span className="section-kicker justify-center">{t.news.kicker}</span>
          <h2 className="section-title">{t.news.title}</h2>
        </Reveal>

        <Stagger className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {displayNews.map((item) => (
            <StaggerItem key={item.id} className="h-full">
              <Card className="app-card group flex h-full flex-col overflow-hidden">
                {(item.image_url || item.video_url) && (
                  <div className="relative h-48 overflow-hidden">
                    {item.video_url ? (
                      isYouTubeURL(item.video_url) ? (
                        <LazyYouTube url={item.video_url} className="h-full w-full" title={pickLocalized(item, "title", locale)} />
                      ) : (
                        <video
                          src={item.video_url}
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                          controls={false}
                          muted
                          preload="metadata"
                          poster={item.image_url || undefined}
                        />
                      )
                    ) : (
                      <img
                        src={item.image_url}
                        alt={pickLocalized(item, "title", locale)}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                        decoding="async"
                        width="378"
                        height="192"
                        sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                        srcSet={`${item.image_url}?w=378&h=192&fit=crop 378w, ${item.image_url}?w=756&h=384&fit=crop 756w`}
                      />
                    )}

                    {/* Event Date Calendar Card */}
                    {item.news_type === 'event' && item.event_date && (
                      <div className="absolute bottom-4 right-4">
                        <div className="min-w-[70px] overflow-hidden rounded-lg border border-border/50 bg-background/95 shadow-lg">
                          <div className="bg-primary px-3 py-1 text-center text-primary-foreground">
                            <div className="text-xs font-semibold uppercase">
                              {t.news.months[new Date(item.event_date).getMonth()]}
                            </div>
                          </div>
                          <div className="px-3 py-2 text-center">
                            <div className="text-2xl font-bold leading-none text-foreground">
                              {format(new Date(item.event_date), 'dd')}
                            </div>
                            <div className="mt-1 text-xs font-medium text-muted-foreground">
                              {format(new Date(item.event_date), 'HH:mm')}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="absolute left-4 top-4">
                      <Badge variant="secondary" className="bg-background/90 text-foreground">
                        <div className="flex items-center gap-1">
                          {getNewsTypeIcon(item.news_type)}
                          {getNewsTypeLabel(item.news_type)}
                        </div>
                      </Badge>
                    </div>
                  </div>
                )}

                <CardHeader className="pb-4">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    {!item.image_url && !item.video_url ? (
                      <Badge variant="secondary">
                        <div className="flex items-center gap-1">
                          {getNewsTypeIcon(item.news_type)}
                          {getNewsTypeLabel(item.news_type)}
                        </div>
                      </Badge>
                    ) : <span />}
                    {item.published_at && (
                      <time dateTime={item.published_at} className="text-sm text-muted-foreground">
                        {format(new Date(item.published_at), 'dd.MM.yyyy')}
                      </time>
                    )}
                  </div>
                  <CardTitle className="text-lg font-semibold leading-snug text-foreground transition-colors group-hover:text-primary">
                    <Link to={`/news/${item.id}`} className="focus-visible:outline-none focus-visible:underline">
                      {pickLocalized(item, "title", locale)}
                    </Link>
                  </CardTitle>
                </CardHeader>

                <CardContent className="mt-auto pt-0">
                  {item.excerpt && (
                    <p className="mb-4 line-clamp-3 text-muted-foreground">{pickLocalized(item, "excerpt", locale)}</p>
                  )}

                  <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                    <Button asChild variant="default" className="h-auto min-h-10 w-full whitespace-normal px-3 py-2 text-sm sm:w-auto sm:flex-1 sm:basis-0">
                      <Link to={`/news/${item.id}`} className="inline-flex items-center justify-center gap-2 text-center">
                        <Eye className="h-4 w-4 shrink-0" aria-hidden="true" />
                        <span className="min-w-0 leading-tight">{t.news.details}</span>
                      </Link>
                    </Button>

                    {item.external_link && (
                      <Button asChild variant="outline" className="h-auto min-h-10 w-full whitespace-normal px-3 py-2 text-sm sm:w-auto sm:flex-1 sm:basis-0">
                        <a href={item.external_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 text-center">
                          <ExternalLink className="h-4 w-4 shrink-0" aria-hidden="true" />
                          <span className="min-w-0 leading-tight">{t.news.readFull}</span>
                        </a>
                      </Button>
                    )}
                  </div>

                </CardContent>
              </Card>
            </StaggerItem>
          ))}
        </Stagger>

        {news && news.length > 6 && (
          <div className="mt-12 text-center">
            <Button asChild variant="outline" size="lg">
              <Link to="/news">{t.news.viewAll}</Link>
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};
